import { useTranslation } from 'react-i18next';
import { pageWidth } from '../../constants/gridConfig'
import { useStore } from '../../store/store';
import NavbarSelector from '../Layout/NavbarSelector';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { SPACE_BOX_SIZE, viewerTypes, getPrimaryDark } from '../../constants/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

import { Button } from 'antd';
import Tooltip from './Tooltip';
import { usePreviewContext } from '../../contexts/PreviewContext';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import BoxActionsPanel from './BoxActionsPanel';
import LabelledRow from './LabelledRow';

const selector =
  (box) =>
    ({
      configuration,
      selectedBoxId,
      updateBox,
      containers,
      currentDocument
    }) => {
      const containerIdx = containers.findIndex((c) => c.columnsIds.includes(box.columnId))
      return {
        sizeSpaceContainer: configuration.sizeSpaceContainer,
        isSelected: selectedBoxId === box.id,
        updateBox,
        containerId: containers[containerIdx]?.id,
        containerIdx,
        accentColor: getPrimaryDark(currentDocument?.type === 'template')
      }
    };
const SpaceBox = ({ box }) => {
  const { t } = useTranslation();
  const {
    sizeSpaceContainer,
    isSelected,
    containerIdx,
    containerId,
    accentColor
  } = useStore(selector(box));
  const appendPages = useStore(({ appendPages }) => appendPages)
  const removeContainer = useStore(({ removeContainer }) => removeContainer)
  const { forceHover } = usePreviewContext();
  const divRef = useRef(null);
  const [isSimulatedSelected, setIsSimulatedSelected] = useState(false);

  const sizeSpaceBox = sizeSpaceContainer.find((space) => space.id === box.content.sizeName)?.value;

  const [isHovered, setIsHovered] = useState(false);
  const effectiveHovered = isHovered || (forceHover && box?.fromViewer === viewerTypes.PREVIEW);

  // `fromViewer` : la box est rendue dans un visualiseur (aperçu, formulaire
  // variables...) et non dans l'éditeur. Seul l'éditeur a l'inspecteur de
  // droite ; les visualiseurs gardent le réglage porté dans la barre du haut.
  const isEditorSelection = !box?.fromViewer && isSelected;
  const isViewerSelection = !!box?.fromViewer && (isSelected || isSimulatedSelected);

  const handleClickOutside = useCallback((event) => {
    if (divRef.current && !divRef.current.contains(event.target) && !event.target?.closest?.('[data-ignore-space-box="true"]')) {
      setIsSimulatedSelected(false);
    }
  }, [setIsSimulatedSelected, divRef]);

  useEffect(() => {
    if (box?.fromViewer === viewerTypes.PREVIEW) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [handleClickOutside, box?.fromViewer]);

  useEffect(() => {
    if (!isSimulatedSelected) {
      setIsHovered(false);
    }
  }, [isSimulatedSelected]);

  return (
    <>
      <div
        ref={divRef}
        onClick={() => {
          if (box.fromViewer === viewerTypes.PREVIEW) {
            setIsSimulatedSelected(true)
          }
        }}
        className='space-box'
        style={{
          height: `${sizeSpaceBox}px`,
          color: "gray",
          width: pageWidth - 80,
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          backgroundColor: effectiveHovered || isSelected ? '#F5F5F5' : 'transparent', // Background change on hover
          outline: isSimulatedSelected ? '2px solid ' + accentColor : undefined,

        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          if (isSimulatedSelected) return;
          setIsHovered(false)
        }}
      >
        {box.fromViewer === viewerTypes.PREVIEW && effectiveHovered && !box?.from_linked_section && <span
          onClick={(e) => {
            e.stopPropagation();
            removeContainer(containerId, () => appendPages(containerIdx - 1))
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 10,
            cursor: 'pointer',
            color: 'red',
            fontSize: 10,
          }}
        >
          &#10060;
        </span>}
        <div className='space-box-icon'
          style={{
            display: effectiveHovered || isSelected ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
          <Tooltip title="Espace">
            <Button
              style={{ width: sizeSpaceBox * 0.9, height: sizeSpaceBox * 0.9, border: 'none', padding: 0, color: "black" }}
              disabled
              icon={
                <span className="fa-layers" style={{ width: `${sizeSpaceBox * 0.9}px` }}>
                  <FontAwesomeIcon
                    icon={icon({ name: 'square-dashed', style: 'light' })}
                    style={{ fontSize: `${sizeSpaceBox * 0.8}px` }}
                  />
                  <FontAwesomeIcon
                    icon={icon({ name: 'arrow-up-right-and-arrow-down-left-from-center', style: 'regular' })}
                    style={{
                      fontSize: `${sizeSpaceBox * 0.37}px`,
                      transform: 'rotate(135deg)',
                    }}
                  />
                </span>
              }
            />
          </Tooltip>
        </div>

      </div>
      {/* Éditeur : le réglage vit dans l'inspecteur de droite, comme celui de
          toutes les autres box. Il partait dans la barre du haut, à côté du
          zoom et de l'état de sauvegarde, loin de la box réglée. */}
      {isEditorSelection && (
        <>
          <BoxFormControlsPortal title={t('space')}>
            <LabelledRow label={t('size')}>
              <SpaceSizeSelector
                selectedBox={box}
                containerIdx={containerIdx}
                fromViewer={box.fromViewer}
              />
            </LabelledRow>
          </BoxFormControlsPortal>
          <BoxActionsPanel />
        </>
      )}
      {/* Visualiseur : pas d'inspecteur de droite, le réglage reste porté dans
          la barre du haut. */}
      {isViewerSelection
        ? ReactDOM.createPortal(
          <SpaceComponenent selectedBox={box} containerIdx={containerIdx} fromViewer={box.fromViewer} />,
          document.getElementById('ComponentPortal')
        )
        : null}
    </>
  )
}

// Choix de la hauteur d'un espace. Partagé par l'inspecteur de droite
// (éditeur) et la barre du haut (aperçu).
export const SpaceSizeSelector = ({ selectedBox, containerIdx, fromViewer }) => {
  const boxId = selectedBox?.id;

  const { t } = useTranslation();
  const updateBox = useStore(({ updateBox }) => updateBox);
  const appendPages = useStore(({ appendPages }) => appendPages)

  const selectSpaceSize = (spaceId) => {
    updateBox(boxId, (box) => {
      box.content.sizeName = spaceId;
    }, {}, () => {
      if (fromViewer === viewerTypes.PREVIEW) {
        appendPages(containerIdx - 1)
      }
    });
  };

  return (
    <NavbarSelector
      key="spaceSizeSelector"
      tooltipContent={t('size')}
      onSelect={selectSpaceSize}
      selectedValue={selectedBox.content.sizeName}
      options={SPACE_BOX_SIZE.map(({ id, name }) => ({ label: t(name), value: id }))}
      data-ignore-space-box="true"
    />
  );
}

export const SpaceComponenent = ({ selectedBox, containerIdx, fromViewer }) => (
  <div className='navbar__formshape-properties-container' data-ignore-space-box="true">
    <div className='navbar__fstop-properties-container'>
      <SpaceSizeSelector
        selectedBox={selectedBox}
        containerIdx={containerIdx}
        fromViewer={fromViewer}
      />
    </div>
  </div>
);

export default React.memo(SpaceBox);
